import React, { useState, useEffect, useRef } from 'react';

// Custom TimeInput component with typing and autosuggestion
export interface TimeInputProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  icon?: string;
  placeholder?: string;
  disabled?: boolean;
  compact?: boolean;
}

const TimeInput: React.FC<TimeInputProps> = ({
  value,
  onChange,
  label,
  icon,
  placeholder = "e.g. 8am, 2:30pm",
  disabled = false,
  compact = false
}) => {
  const [inputValue, setInputValue] = useState<string>(value);
  const [showSuggestions, setShowSuggestions] = useState<boolean>(false);
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Generate 15-minute intervals from 5 AM to 11 PM
  const timeOptions = (() => {
    const options = [];
    for (let hour = 5; hour <= 23; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const hourDisplay = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        const ampm = hour >= 12 ? 'PM' : 'AM';
        options.push(`${hourDisplay}:${minute.toString().padStart(2, '0')} ${ampm}`);
      }
    }
    return options;
  })();

  // Normalize time input to standard format
  const normalizeTimeInput = (input: string): string => {
    if (!input) return input;

    const cleaned = input.replace(/[^\d:apm\s]/gi, '').toLowerCase().trim();

    // Handle formats like "8am", "8:30pm", "14:30", etc.
    const timeRegex = /^(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)?$/i;
    const match = cleaned.match(timeRegex);

    if (match) {
      let hour = parseInt(match[1]);
      let minute = parseInt(match[2] || '0');
      const period = match[3]?.toLowerCase();

      // Validate minute
      if (isNaN(minute) || minute > 59) {
        minute = 0;
      }

      // Validate hour
      if (isNaN(hour) || hour < 1 || hour > 24) {
        return input;
      }

      // Convert 24-hour to 12-hour format
      if (!period && hour > 12) {
        hour = hour - 12;
        return `${hour}:${minute.toString().padStart(2, '0')} PM`;
      } else if (!period && hour === 0) {
        return `12:${minute.toString().padStart(2, '0')} AM`;
      } else if (!period) {
        const ampm = hour === 12 ? 'PM' : 'AM';
        return `${hour}:${minute.toString().padStart(2, '0')} ${ampm}`;
      }

      // Handle explicit AM/PM
      if (period === 'pm' && hour !== 12) {
        return `${hour}:${minute.toString().padStart(2, '0')} PM`;
      } else if (period === 'am' && hour === 12) {
        return `12:${minute.toString().padStart(2, '0')} AM`;
      } else if (period === 'am') {
        return `${hour}:${minute.toString().padStart(2, '0')} AM`;
      } else if (period === 'pm') {
        return `${hour}:${minute.toString().padStart(2, '0')} PM`;
      }
    }

    return input; // Return original if can't parse
  };

  // Find the closest matching time option
  const findClosestTimeMatch = (input: string): number => {
    if (!input) return -1;

    const normalized = normalizeTimeInput(input);

    // First try exact match
    const exactIndex = timeOptions.findIndex(time => time === normalized);
    if (exactIndex !== -1) return exactIndex;

    // Then try partial matches
    const lowerInput = input.toLowerCase();
    for (let i = 0; i < timeOptions.length; i++) {
      const time = timeOptions[i].toLowerCase();
      if (time.includes(lowerInput) || time.replace(/[:\s]/g, '').includes(lowerInput.replace(/[:\s]/g, ''))) {
        return i;
      }
    }

    return -1;
  };

  // Get the index of the current value in timeOptions
  const getCurrentValueIndex = (): number => {
    return timeOptions.findIndex(time => time === value);
  };

  useEffect(() => {
    setInputValue(value);
  }, [value]);

  useEffect(() => {
    // When input changes, find the closest match and highlight it
    const closestIndex = findClosestTimeMatch(inputValue);
    setHighlightedIndex(closestIndex);

    // Always show all suggestions when dropdown is open
    if (showSuggestions) {
      setSuggestions(timeOptions);
    }
  }, [inputValue, showSuggestions]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInputValue(newValue);
    setShowSuggestions(true);
  };

  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Delay hiding suggestions to allow clicking on them
    setTimeout(() => {
      if (!dropdownRef.current?.contains(document.activeElement)) {
        setShowSuggestions(false);
        // Try to normalize the input when losing focus
        const normalized = normalizeTimeInput(inputValue);
        if (normalized !== inputValue && timeOptions.includes(normalized)) {
          setInputValue(normalized);
          onChange(normalized);
        }
      }
    }, 150);
  };

  const handleInputFocus = () => {
    if (disabled) return;

    setShowSuggestions(true);
    setSuggestions(timeOptions); // Show all options

    // Scroll to current value when opening dropdown
    const currentIndex = getCurrentValueIndex();
    if (currentIndex !== -1) {
      setHighlightedIndex(currentIndex);
      // Scroll to the current value after dropdown renders
      setTimeout(() => {
        const dropdown = dropdownRef.current;
        const highlightedItem = dropdown?.children[currentIndex] as HTMLElement;
        if (dropdown && highlightedItem) {
          const dropdownHeight = dropdown.clientHeight;
          const itemHeight = highlightedItem.offsetHeight;
          const itemTop = highlightedItem.offsetTop;
          const scrollTop = itemTop - (dropdownHeight / 2) + (itemHeight / 2);
          dropdown.scrollTop = Math.max(0, scrollTop);
        }
      }, 10);
    }
  };

  const handleSuggestionClick = (time: string) => {
    setInputValue(time);
    onChange(time);
    setShowSuggestions(false);
    inputRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!showSuggestions || suggestions.length === 0) {
      if (e.key === 'Enter') {
        const normalized = normalizeTimeInput(inputValue);
        if (timeOptions.includes(normalized)) {
          setInputValue(normalized);
          onChange(normalized);
        }
      }
      return;
    }

    const scrollToHighlighted = (index: number) => {
      setTimeout(() => {
        const dropdown = dropdownRef.current;
        const highlightedItem = dropdown?.children[index] as HTMLElement;
        if (dropdown && highlightedItem) {
          const dropdownHeight = dropdown.clientHeight;
          const itemHeight = highlightedItem.offsetHeight;
          const itemTop = highlightedItem.offsetTop;
          const dropdownScrollTop = dropdown.scrollTop;

          // Check if item is above visible area
          if (itemTop < dropdownScrollTop) {
            dropdown.scrollTop = itemTop;
          }
          // Check if item is below visible area
          else if (itemTop + itemHeight > dropdownScrollTop + dropdownHeight) {
            dropdown.scrollTop = itemTop + itemHeight - dropdownHeight;
          }
        }
      }, 0);
    };

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        const nextIndex = highlightedIndex < suggestions.length - 1 ? highlightedIndex + 1 : 0;
        setHighlightedIndex(nextIndex);
        scrollToHighlighted(nextIndex);
        break;
      case 'ArrowUp':
        e.preventDefault();
        const prevIndex = highlightedIndex > 0 ? highlightedIndex - 1 : suggestions.length - 1;
        setHighlightedIndex(prevIndex);
        scrollToHighlighted(prevIndex);
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightedIndex >= 0) {
          handleSuggestionClick(suggestions[highlightedIndex]);
        } else {
          const normalized = normalizeTimeInput(inputValue);
          if (timeOptions.includes(normalized)) {
            setInputValue(normalized);
            onChange(normalized);
            setShowSuggestions(false);
          }
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setHighlightedIndex(-1);
        break;
    }
  };

  // Compact mode: minimal styling for table cells
  if (compact) {
    return (
      <div className="position-relative time-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="form-control form-control-sm"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled}
          style={{ fontSize: "0.85rem" }}
        />

        {showSuggestions && !disabled && suggestions.length > 0 && (
          <div
            ref={dropdownRef}
            className="position-absolute w-100 time-input-suggestions"
            style={{
              top: '100%',
              zIndex: 2000,
              backgroundColor: 'white',
              border: '1px solid #dee2e6',
              borderRadius: '0.375rem',
              maxHeight: '200px',
              overflowY: 'auto',
              boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
            }}
          >
            {suggestions.map((time, index) => (
              <div
                key={time}
                className={`suggestion-item px-2 py-1 ${index === highlightedIndex ? 'bg-primary text-white' : ''}`}
                onMouseDown={() => handleSuggestionClick(time)}
                onMouseEnter={() => setHighlightedIndex(index)}
                style={{ cursor: 'pointer', fontSize: "0.85rem" }}
              >
                {time}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Normal mode: full styling with label and helper text
  return (
    <div className="mb-3 position-relative time-input-wrapper">
      {label && (
        <label className="form-label fw-bold">
          {icon && <i className={`fa ${icon} me-1`}></i>}{label}
        </label>
      )}
      <div className="position-relative">
        <input
          ref={inputRef}
          type="text"
          className="form-control"
          value={inputValue}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
          onFocus={handleInputFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          autoComplete="off"
          disabled={disabled}
        />

        {showSuggestions && !disabled && suggestions.length > 0 && (
          <div
            ref={dropdownRef}
            className="position-absolute w-100 time-input-suggestions"
            style={{
              top: '100%',
              zIndex: 2000,
              backgroundColor: 'white',
              border: '1px solid #dee2e6',
              borderRadius: '0.375rem',
              maxHeight: '200px',
              overflowY: 'auto',
              boxShadow: '0 0.5rem 1rem rgba(0, 0, 0, 0.15)'
            }}
          >
            {suggestions.map((time, index) => {
              // Convert 12-hour format to 24-hour for the small text
              const convert12to24 = (time12: string): string => {
                const match = time12.match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
                if (!match) return time12;

                let hour = parseInt(match[1]);
                const minute = match[2];
                const period = match[3].toUpperCase();

                if (period === 'PM' && hour !== 12) hour += 12;
                if (period === 'AM' && hour === 12) hour = 0;

                return `${hour.toString().padStart(2, '0')}:${minute}`;
              };

              return (
                <div
                  key={time}
                  className={`suggestion-item p-2 d-flex justify-content-between ${index === highlightedIndex ? 'bg-primary text-white' : ''
                    }`}
                  onMouseDown={() => handleSuggestionClick(time)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  style={{ cursor: 'pointer' }}
                >
                  <span className="fw-medium">{time}</span>
                  <small className={index === highlightedIndex ? 'text-white-50' : 'text-muted'}>
                    {convert12to24(time)}
                  </small>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <small className="text-muted">
        Type time like "8am", "2:30pm", or "14:30". Use ↑↓ to navigate suggestions.
      </small>
    </div>
  );
};

export default TimeInput;
